import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppNav from './AppNav';
import TodoMenu from './TodoMenu';
import TodoList from './TodoList';
import DrawerTodoDetail from './DrawerTodoDetail';
import DrawerTagDetail from './DrawerTagDetail';
import DrawerHabitDetail from './DrawerHabitDetail';
import { Todo, TodoTagEntity, HabitEntity } from '../types/data';
import { getLocalDateString } from '../data/todoData';
import { useTodos } from '../hooks/useTodos';
import { useTags } from '../hooks/useTags';
import { useHabits } from '../hooks/useHabits';
import { useTodoNavigationSummary } from '../hooks/useTodoNavigationSummary';
import { useTodoRealtimeSync } from '../hooks/useTodoRealtimeSync';
import { Habit } from '../data/habitData';
import { todoLocalRepo } from '../utils/localDb/todoLocalRepo';
import { getShoppingHabitId, isShoppingHabitId } from '../utils/habits';
import {
  getTodayDateString,
  urlFormatToIsoDate,
  isoDateToUrlFormat,
  isValidDateUrl,
  handleLeapYearRedirect,
  createTokyoDateTime,
  getTokyoDateParts,
  getTodayTokyoDateString,
  startOfTokyoDate,
} from '../utils/dateUtils';

type ViewMode = 'date' | 'tag' | 'habit';
const BACKEND = (process.env.REACT_APP_BACKEND || 'local').toLowerCase();

function PageTodo() {
  const { date, tagId, habitId } = useParams<{ date?: string; tagId?: string; habitId?: string }>();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState<Date | undefined>(undefined);
  const [viewMode, setViewMode] = useState<ViewMode>('date');
  const [selectedTagId, setSelectedTagId] = useState<string | null>(null);
  const [selectedHabitId, setSelectedHabitId] = useState<string | null>(null);
  const [visibleTodos, setVisibleTodos] = useState<Todo[]>([]);
  
  // useTodosフックを使用
  const {
    todos,
    syncRevision,
    refreshFromServer: refreshTodos,
    createTodo,
    updateTodo,
    deleteTodo,
    toggleTodoComplete
  } = useTodos();

  // useTagsフックを使用
  const {
    activeTags,
    getTagById,
    getHabitTagIds,
    updateTag,
    deleteTag,
    createNewTag,
    reorderTags,
    refreshFromServer: refreshTags
  } = useTags();

  // useHabitsフックを使用（習慣メニュー導線・編集）
  const { deleteHabit, getHabitById, updateHabit, refreshFromServer: refreshHabits } = useHabits();
  const refreshAfterRealtimeNotification = useCallback(async () => {
    refreshTodos();
    refreshTags();
    refreshHabits();
  }, [refreshHabits, refreshTags, refreshTodos]);
  useTodoRealtimeSync(refreshAfterRealtimeNotification);
  const { summary: todoNavigationSummary } = useTodoNavigationSummary(currentMonth, todos, syncRevision);

  // Todoドロワーの状態管理
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [todoSaveError, setTodoSaveError] = useState<string | null>(null);
  const [selectedTodo, setSelectedTodo] = useState<Todo | null>(null);

  // タグドロワーの状態管理
  const [isTagDrawerOpen, setIsTagDrawerOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState<TodoTagEntity | null>(null);

  // 習慣ドロワーの状態管理
  const [isHabitDrawerOpen, setIsHabitDrawerOpen] = useState(false);
  const [selectedHabit, setSelectedHabit] = useState<HabitEntity | null>(null);

  useEffect(() => {
    // タグ別表示の場合
    if (tagId) {
      setViewMode('tag');
      // 'uncategorized' は null に変換
      setSelectedTagId(tagId === 'uncategorized' ? null : tagId);
      setSelectedDate(null);
      setSelectedHabitId(null);
      return;
    }

    // 習慣別表示の場合
    if (habitId) {
      setViewMode('habit');
      setSelectedHabitId(habitId);
      setSelectedDate(null);
      setSelectedTagId(null);
      return;
    }

    // 日付別表示の場合
    setViewMode('date');
    setSelectedTagId(null);
    setSelectedHabitId(null);

    if (!date) {
      // ルートアクセスの場合、今日の日付にリダイレクト
      const todayUrl = getTodayDateString();
      navigate(`/${todayUrl}/`, { replace: true });
      return;
    }

    // 閏年チェック（YYYY0229の場合のみ）
    const leapYearRedirect = handleLeapYearRedirect(date);
    if (leapYearRedirect) {
      navigate(`/${leapYearRedirect}/`, { replace: true });
      return;
    }

    // 有効な日付かチェック
    if (!isValidDateUrl(date)) {
      // 無効な日付の場合、今日の日付にリダイレクト
      const todayUrl = getTodayDateString();
      navigate(`/${todayUrl}/`, { replace: true });
      return;
    }

    // 有効な日付の場合、selectedDateを設定し、カレンダーの表示月も更新
    const isoDate = urlFormatToIsoDate(date);
    setSelectedDate(isoDate);

    // URLの日付に対応する月をカレンダーに表示
    setCurrentMonth(startOfTokyoDate(isoDate));
  }, [date, tagId, habitId, navigate]);

  const handleDateSelect = (newDate: string) => {
    setSelectedDate(newDate);
    // カレンダーで日付を選択した場合、URLも更新
    const urlDate = isoDateToUrlFormat(newDate);
    navigate(`/${urlDate}/`);
  };

  const handleTagSelect = (tagId: string | null) => {
    if (tagId === null) {
      // 「未分類」を選択
      navigate('/tag/uncategorized/');
    } else {
      navigate(`/tag/${tagId}/`);
    }
  };

  const handleHabitSelect = (habitId: string) => {
    navigate(`/habit/${habitId}/`);
  };

  // DrawerTodoDetailで日付が変更された時のハンドラー
  // 挙動: 日付ビューのときのみカレンダータブ（URL）を切り替える。
  // タグ・習慣ビューから開いた場合は一覧を保つため、URL遷移しない。
  const handleTodoDateChange = (newDate: string) => {
    if (viewMode === 'date') {
      setSelectedDate(newDate);
      const urlDate = isoDateToUrlFormat(newDate);
      navigate(`/${urlDate}/`);
    }
  };

  const handleToggleTodo = async (todoId: string) => {
    try {
      const saved = await toggleTodoComplete(todoId);
      if (saved) setVisibleTodos(prev => prev.map(todo => todo.id === saved.id ? saved : todo));
    } catch (error) { setTodoSaveError(error instanceof Error ? error.message : '保存に失敗しました。'); }
  };

  const handleShowTodoDetail = async (todoId: string) => {
    const todo = BACKEND === 'supabase'
      ? await todoLocalRepo.getTodoById(todoId)
      : todos.find(t => t.id === todoId);
    if (todo) {
      setSelectedTodo(todo);
      setIsDrawerOpen(true);
    }
  };

  // ドロワーを閉じる
  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedTodo(null);
  };

  // Todo保存
  const handleSaveTodo = async (updatedTodo: Todo, original?: Todo): Promise<Todo | undefined> => {
    const saved = await updateTodo(updatedTodo.id, updatedTodo, original);
    if (saved) {
      setVisibleTodos(prev => prev.map(todo => todo.id === saved.id ? saved : todo));
      setSelectedTodo(previous => previous?.id === saved.id ? saved : previous);
    }
    return saved;
  };

  // 新規Todo作成
  const handleCreateTodo = async (newTodo: Omit<Todo, 'id'> & { id?: string }) => {
    const createdTodo = await createTodo(newTodo);
    return createdTodo;
  };

  // Todo削除
  const handleDeleteTodo = async (todoId: string) => {
    try {
      await deleteTodo(todoId, visibleTodos.find(todo => todo.id === todoId));
      setVisibleTodos(prev => prev.filter(todo => todo.id !== todoId));
      if (selectedTodo?.id === todoId) { setIsDrawerOpen(false); setSelectedTodo(null); }
    } catch (error) { setTodoSaveError(error instanceof Error ? error.message : '削除に失敗しました。'); }
  };

  // Todo複製
  const handleDuplicateTodo = async (todo: Todo) => {
    await createTodo({
      title: `${todo.title} (コピー)`,
      body: todo.body,
      startAt: todo.startAt,
      done: false,
      canceled: false,
      deleted: false,
      doneAt: undefined
    });
  };

  // 新規Todo作成
  const handleCreateNewTodo = () => {
    // 初期値設定
    const now = new Date();
    const nowTokyo = getTokyoDateParts(now);

    // startAtの設定
    let startAt: string;
    if (viewMode === 'date' && selectedDate) {
      // 日付別表示: 選択された日付を使用
      const [year, month, day] = selectedDate.split('-').map(Number);
      const isToday = selectedDate === getTodayTokyoDateString();

      if (isToday) {
        // 今日の場合：現在時刻+1時間、分は00
        const target = new Date(createTokyoDateTime(nowTokyo).getTime() + 60 * 60 * 1000);
        const targetParts = getTokyoDateParts(target);
        startAt = createTokyoDateTime({ ...targetParts, minute: 0, second: 0 }).toISOString();
      } else {
        // 今日以外の場合：9:00 AM
        startAt = createTokyoDateTime({ year, month, day, hour: 9 }).toISOString();
      }
    } else {
      // タグ別・習慣別表示: 今日の現在時刻+1時間
      const target = new Date(createTokyoDateTime(nowTokyo).getTime() + 60 * 60 * 1000);
      const targetParts = getTokyoDateParts(target);
      startAt = createTokyoDateTime({ ...targetParts, minute: 0, second: 0 }).toISOString();
    }

    // 空のTodoを作成してドロワーを開く
    const newTodo: Todo = {
      id: '', // DrawerTodoDetailで新規判定用
      title: '',
      body: '',
      startAt: startAt,
      done: false,
      canceled: false,
      deleted: false,
      doneAt: undefined,
      createdAt: now.toISOString(),
      modifiedAt: now.toISOString()
    };

    // タグ別表示の場合、該当タグを設定
    if (viewMode === 'tag' && selectedTagId !== null) {
      newTodo.todoTagIds = [selectedTagId];
    }

    // 習慣別表示の場合、該当習慣IDを設定
    if (viewMode === 'habit' && selectedHabitId) {
      newTodo.habitId = selectedHabitId;
      const defaultTagIds = getHabitTagIds(selectedHabitId);
      if (defaultTagIds.length > 0) {
        newTodo.todoTagIds = defaultTagIds;
      }
    }

    setSelectedTodo(newTodo);
    setIsDrawerOpen(true);
  };

  // タグ編集を開く
  const handleEditTag = () => {
    if (!selectedTagId) return;
    const tag = getTagById(selectedTagId);
    if (tag) {
      setSelectedTag(tag);
      setIsTagDrawerOpen(true);
    }
  };

  // タグドロワーを閉じる
  const handleCloseTagDrawer = () => {
    setIsTagDrawerOpen(false);
    setSelectedTag(null);
  };

  // タグ保存
  const handleSaveTag = (updatedTag: TodoTagEntity) => {
    updateTag(updatedTag.id, updatedTag);
  };

  // タグ新規作成
  const handleCreateTag = async (newTagInput: Omit<TodoTagEntity, 'id'>) => {
    const created = await createNewTag(newTagInput.title, newTagInput.body);
    // 新規作成後、ドロワーに作成済みタグを反映（id付き）
    setSelectedTag(created);
  };

  // タグ削除
  const handleDeleteTag = async (tagId: string) => {
    // 1. ドロワーを閉じる
    setIsTagDrawerOpen(false);
    setSelectedTag(null);

    // 2. 削除されたタグが現在表示中の場合、即座にselectedTagIdをクリア
    if (selectedTagId === tagId) {
      setSelectedTagId(null);
      setViewMode('tag');
    }

    // 3. タグを論理削除
    deleteTag(tagId);

    // 4. 関連TODOからタグIDを削除
    const relatedTodos = BACKEND === 'supabase'
      ? await todoLocalRepo.listTodosByTag(tagId)
      : todos.filter(todo => todo.todoTagIds?.includes(tagId));

    relatedTodos.forEach(todo => {
      if (todo.todoTagIds?.includes(tagId)) {
        const updatedTagIds = todo.todoTagIds.filter(id => id !== tagId);
        updateTodo(todo.id, { todoTagIds: updatedTagIds }, todo).catch(error => setTodoSaveError(String(error)));
      }
    });

    // 5. 削除されたタグが現在表示中の場合、未分類ページに遷移
    if (selectedTagId === tagId) {
      navigate('/tag/uncategorized/', { replace: true });
    }
    // それ以外の場合は現在のページを維持（タグ一覧は自動更新される）
  };

  // Habit <-> HabitEntity 変換
  const habitToEntity = (h: Habit): HabitEntity => ({
    id: h.id,
    title: h.title,
    body: h.body,
    created_at: h.createdAt,
    modified_at: h.modifiedAt,
    is_deleted: h.deleted,
    last_done_on: h.lastDoneOn,
    done_cycle_date: h.doneCycleDate,
  });
  // 習慣編集（Drawer を開く）
  const handleEditHabit = () => {
    if (!selectedHabitId) return;
    const h = getHabitById(selectedHabitId);
    if (!h) return;
    setSelectedHabit(habitToEntity(h));
    setIsHabitDrawerOpen(true);
  };

  // 習慣削除（確認ダイアログ → 削除 → ビュー更新）
  const handleDeleteHabit = (habitId: string) => {
    if (isShoppingHabitId(habitId)) return; // 念のため保護
    const ok = window.confirm('この習慣を削除しますか？');
    if (!ok) return;

    const result = deleteHabit(habitId);
    // Drawer が開いていたら閉じる
    setIsHabitDrawerOpen(false);
    setSelectedHabit(null);

    if (result && selectedHabitId === habitId) {
      const shoppingId = getShoppingHabitId();
      if (shoppingId) navigate(`/habit/${shoppingId}/`, { replace: true });
    }
  };

  const getTodosForCurrentViewFromState = useCallback((): Todo[] => {
    if (viewMode === 'date') {
      // 日付別表示
      return selectedDate ? todos.filter(todo => {
        const todoLocalDate = getLocalDateString(todo.startAt);
        return todoLocalDate === selectedDate && !todo.deleted;
      }) : [];
    } else if (viewMode === 'tag') {
      // タグ別表示
      if (selectedTagId === null) {
        // 未分類
        return todos.filter(todo =>
          !todo.deleted &&
          (!todo.todoTagIds || todo.todoTagIds.length === 0)
        );
      } else {
        return todos.filter(todo =>
          !todo.deleted &&
          todo.todoTagIds?.includes(selectedTagId)
        );
      }
    } else if (viewMode === 'habit') {
      // 習慣別表示
      return todos.filter(todo =>
        !todo.deleted &&
        todo.habitId === selectedHabitId
      );
    }
    return [];
  }, [selectedDate, selectedHabitId, selectedTagId, todos, viewMode]);

  useEffect(() => {
    let alive = true;

    if (BACKEND !== 'supabase') {
      setVisibleTodos(getTodosForCurrentViewFromState());
      return;
    }

    (async () => {
      try {
        let rows: Todo[] = [];
        if (viewMode === 'date') {
          rows = selectedDate ? await todoLocalRepo.listTodosByDate(selectedDate) : [];
        } else if (viewMode === 'tag') {
          rows = selectedTagId === null
            ? await todoLocalRepo.listUncategorizedTodos()
            : await todoLocalRepo.listTodosByTag(selectedTagId);
        } else if (viewMode === 'habit') {
          rows = selectedHabitId
            ? await todoLocalRepo.listTodosByHabit(selectedHabitId)
            : [];
        }
        if (alive) setVisibleTodos(rows);
      } catch (e) {
        console.error('Failed to load visible todos from IndexedDB:', e);
        if (alive) setVisibleTodos([]);
      }
    })();

    return () => {
      alive = false;
    };
  }, [viewMode, selectedDate, selectedTagId, selectedHabitId, todos, syncRevision, getTodosForCurrentViewFromState]);

  const getSameDateVisibleTodos = (): Todo[] => {
    if (!selectedDate) return [];
    return visibleTodos
      .filter(todo => {
        const todoLocalDate = getLocalDateString(todo.startAt);
        return todoLocalDate === selectedDate && !todo.deleted;
      })
      .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());
  };

  // 前の予定に移動
  const handlePreviousTodo = () => {
    if (!selectedTodo || !selectedDate) return;

    const sameDateTodos = getSameDateVisibleTodos();
    
    const currentIndex = sameDateTodos.findIndex(todo => todo.id === selectedTodo.id);
    if (currentIndex > 0) {
      const previousTodo = sameDateTodos[currentIndex - 1];
      setSelectedTodo(previousTodo);
    }
  };

  // 次の予定に移動
  const handleNextTodo = () => {
    if (!selectedTodo || !selectedDate) return;
    
    const sameDateTodos = getSameDateVisibleTodos();
    
    const currentIndex = sameDateTodos.findIndex(todo => todo.id === selectedTodo.id);
    if (currentIndex < sameDateTodos.length - 1) {
      const nextTodo = sameDateTodos[currentIndex + 1];
      setSelectedTodo(nextTodo);
    }
  };

  // 前の予定が存在するかチェック
  const hasPreviousTodo = () => {
    if (!selectedTodo || !selectedDate) return false;
    
    const sameDateTodos = getSameDateVisibleTodos();
    
    const currentIndex = sameDateTodos.findIndex(todo => todo.id === selectedTodo.id);
    return currentIndex > 0;
  };

  // 次の予定が存在するかチェック
  const hasNextTodo = () => {
    if (!selectedTodo || !selectedDate) return false;
    
    const sameDateTodos = getSameDateVisibleTodos();
    
    const currentIndex = sameDateTodos.findIndex(todo => todo.id === selectedTodo.id);
    return currentIndex < sameDateTodos.length - 1;
  };

  return (
    <div className="app">
      {todoSaveError && <div role="alert">{todoSaveError}<button onClick={() => setTodoSaveError(null)}>閉じる</button></div>}
      <AppNav activeItem="todo" />

      <div className="app-main">
        {/* Content Area - 最大幅を定義するコンテナ */}
        <div className="content-area">
          {/* Content Nav - 固定エリア */}
          <div className="content-nav">
            <TodoMenu
              selectedDate={selectedDate}
              onDateSelect={handleDateSelect}
              todos={BACKEND === 'supabase' ? [] : todos}
              navigationSummary={todoNavigationSummary}
              currentMonth={currentMonth}
              onCurrentMonthChange={setCurrentMonth}
              onTagSelect={handleTagSelect}
              selectedTagId={selectedTagId}
              onReorderTags={reorderTags}
              onHabitSelect={handleHabitSelect}
              selectedHabitId={selectedHabitId}
              viewMode={viewMode}
              tags={activeTags}
            />
          </div>

          {/* Content Main - スクロールエリア */}
          <div className="content-main">
            <TodoList
              todos={visibleTodos}
              selectedDate={selectedDate}
              viewMode={viewMode}
              selectedTagId={selectedTagId}
              selectedHabitId={selectedHabitId}
              navigationSummary={todoNavigationSummary}
              onToggleTodo={handleToggleTodo}
              onShowTodoDetail={handleShowTodoDetail}
              onDelete={handleDeleteTodo}
              selectedTodoId={selectedTodo?.id || null}
              onCreateNewTodo={handleCreateNewTodo}
              onEditTag={handleEditTag}
              onDeleteTag={handleDeleteTag}
              onEditHabit={handleEditHabit}
              onDeleteHabit={handleDeleteHabit}
              isDrawerOpen={isDrawerOpen}
            />
          </div>
        </div>

        {/* Todo詳細ドロワー */}
        <DrawerTodoDetail
          todo={selectedTodo || undefined}
          isOpen={isDrawerOpen}
          onClose={handleCloseDrawer}
          onSave={handleSaveTodo}
          onCreate={handleCreateTodo}
          onCreated={created => setSelectedTodo(previous => previous?.id === '' ? created : previous)}
          onDelete={handleDeleteTodo}
          onDuplicate={handleDuplicateTodo}
          onPreviousTodo={handlePreviousTodo}
          onNextTodo={handleNextTodo}
          hasPreviousTodo={hasPreviousTodo()}
          hasNextTodo={hasNextTodo()}
          onDateChange={handleTodoDateChange}
          viewMode={viewMode}
        />

        {/* タグ詳細ドロワー */}
        <DrawerTagDetail
          tag={selectedTag || undefined}
          isOpen={isTagDrawerOpen}
          onClose={handleCloseTagDrawer}
          onSave={handleSaveTag}
          onCreate={handleCreateTag}
          onDelete={handleDeleteTag}
        />

        {/* 習慣詳細ドロワー */}
        <DrawerHabitDetail
          habit={selectedHabit || undefined}
          isOpen={isHabitDrawerOpen}
          onClose={() => {
            setIsHabitDrawerOpen(false);
            setSelectedHabit(null);
          }}
          onSave={(updated) => {
            // HabitEntity -> updateHabit
            const updates: Partial<Omit<Habit, 'id' | 'createdAt'>> = {
              title: updated.title,
              body: updated.body,
              modifiedAt: updated.modified_at,
              deleted: updated.is_deleted,
              lastDoneOn: updated.last_done_on,
              doneCycleDate: updated.done_cycle_date,
            };
            updateHabit(updated.id, updates);
          }}
          onDelete={handleDeleteHabit}
        />
      </div>
    </div>
  );
}

export default PageTodo;
